import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { API_URL } from '../utils/axios-config';
import { hasAdminPanelAccess, ADMIN_ROLES } from '../config/adminRoles';

const AuthContext = createContext(null);

async function fetchAdminSession(firebaseUser) {
  const token = await firebaseUser.getIdToken();
  let response;
  try {
    response = await fetch(`${API_URL}/admin/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (networkErr) {
    const hint = import.meta.env.DEV
      ? ` Impossible de joindre ${API_URL} (backend démarré ? CORS ?).`
      : '';
    throw new Error(`Réseau: ${(networkErr && networkErr.message) || 'échec'}.${hint}`);
  }

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body.error || body.message || '';
    } catch {
      try { detail = await response.text(); } catch { /* ignore */ }
    }
    if (response.status === 401) {
      throw new Error(
        `Auth refusée (401)${detail ? ` — ${detail}` : ''}. ` +
        'Vérifiez que le backend utilise le même Auth Firebase (émulateur : FIREBASE_AUTH_EMULATOR_HOST + émulateur lancé).'
      );
    }
    if (response.status === 403) {
      throw new Error(
        `Accès admin refusé (403)${detail ? ` — ${detail}` : ''}. ` +
        'En local, le backend doit tourner avec NODE_ENV=development (ex. npm run dev) pour le bypass admin.'
      );
    }
    throw new Error(`Erreur /admin/me (${response.status})${detail ? ` — ${detail}` : ''}`);
  }

  const data = await response.json();
  return {
    adminRole: data.adminRole || null,
    isAdmin: hasAdminPanelAccess(data.adminRole),
    isFullAdmin: data.isFullAdmin ?? data.adminRole === ADMIN_ROLES.ADMIN,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFullAdmin, setIsFullAdmin] = useState(false);
  const [checking, setChecking] = useState(true);

  const refreshAdminSession = useCallback(async (firebaseUser) => {
    const targetUser = firebaseUser ?? auth.currentUser;
    if (!targetUser) {
      setAdminRole(null);
      setIsAdmin(false);
      setIsFullAdmin(false);
      return { adminRole: null, isAdmin: false, isFullAdmin: false };
    }

    try {
      const session = await fetchAdminSession(targetUser);
      setAdminRole(session.adminRole);
      setIsAdmin(session.isAdmin);
      setIsFullAdmin(session.isFullAdmin);
      return session;
    } catch (error) {
      console.error('Erreur vérification admin:', error);
      setAdminRole(null);
      setIsAdmin(false);
      setIsFullAdmin(false);
      return {
        adminRole: null,
        isAdmin: false,
        isFullAdmin: false,
        error: error?.message || 'Erreur vérification admin',
      };
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setChecking(true);
      setUser(firebaseUser);

      if (firebaseUser) {
        await refreshAdminSession(firebaseUser);
      } else {
        setAdminRole(null);
        setIsAdmin(false);
        setIsFullAdmin(false);
      }

      setChecking(false);
    });

    return () => unsubscribe();
  }, [refreshAdminSession]);

  const logout = useCallback(async () => {
    await signOut(auth);
    setUser(null);
    setAdminRole(null);
    setIsAdmin(false);
    setIsFullAdmin(false);
  }, []);

  const value = {
    user,
    adminRole,
    isAdmin,
    isFullAdmin,
    checking,
    refreshAdminSession,
    logout,
    isUserAdmin: async () => {
      const session = await refreshAdminSession();
      return session.isAdmin;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
